import { ConversionCopy } from "../../pages/[region]/[lang]/product/[slug]";
import { collectionPage } from "../link-builders";
import { ShippingCopy } from "../sanity-schema";
import {
  isValidShopCard,
  ShopCardPropsWithSlug,
  toShopCardProps,
} from "../transformers/shop-card";
import { Dialect } from "../utils/locale";
import {
  publicShopCollectionProductsIndex,
  shopCollectionProductsIndex,
} from "./indices";
import { AlgoliaShopCollectionProduct } from "./types";

export type AlgoliaCollectionPreview = {
  name: string;
  url: string;
  slug: string;
  shopCards: ShopCardPropsWithSlug[];
  query: string;
  queryID: string;
  cta?: {
    text: string;
    url: string;
  };
};

type AlgoliaFacetFilters = ReadonlyArray<readonly string[] | string>;

const collectionPreviewAttributes = [
  "bestValueVariant",
  "brandName",
  "descriptionV2",
  // Shop PLP filters map Algolia `facets` onto each card's facetFilters
  "facets",
  "flavorProfile",
  "forSale",
  "images",
  "isGiftCard",
  "name",
  "objectID",
  "optionTypes",
  "primaryShopCollectionName",
  "processing",
  "quickSubscribeCard",
  "roastLevel",
  "shopCard",
  "slug",
  "soldOutCtas",
  "subscription",
  "subscriptionOptions",
  "subscriptionType",
  "tags",
  "variants",
];

type GetCollectionPreviewProps = Dialect & {
  collectionName: string;
  collectionSlug: string;
  urlQueryParams?: string;
  facetFilters?: AlgoliaFacetFilters;
  isPublic?: boolean;
  shippingCopy?: ShippingCopy;
  copy?: ConversionCopy;
  ctaLabel?: string;
  showShopcardDetail?: boolean;
};

/** Query Algolia for the top three items in the collection */
export const getCollectionPreview = async ({
  collectionName,
  collectionSlug,
  urlQueryParams,
  facetFilters,
  lang,
  region,
  isPublic = false,
  copy,
  shippingCopy,
  ctaLabel,
  showShopcardDetail,
}: GetCollectionPreviewProps): Promise<AlgoliaCollectionPreview> => {
  const index = isPublic
    ? publicShopCollectionProductsIndex
    : shopCollectionProductsIndex;
  const products = await index.search<AlgoliaShopCollectionProduct>("", {
    facetFilters: [
      [`shopCollectionSlug:${collectionSlug}`],
      ["forSale:true"],
      ...(facetFilters || []),
    ],
    attributesToRetrieve: collectionPreviewAttributes,
    clickAnalytics: true,
  });

  const baseUrl = collectionPage({ region, lang, slug: collectionSlug });

  const url = urlQueryParams ? `${baseUrl}${urlQueryParams}` : baseUrl;

  return {
    name: collectionName,
    query: collectionSlug,
    queryID: products?.queryID,
    shopCards: products?.hits?.filter(isValidShopCard)?.map((product, idx) =>
      toShopCardProps({
        data: product,
        copy,
        shippingCopy,
        lang,
        region,
        props: {
          collectionSlug,
          position: idx,
          queryID: products?.queryID,
          index: index.indexName,
        },
        showShopcardDetail,
      }),
    ),
    slug: collectionSlug,
    url,
    cta: ctaLabel
      ? {
          text: ctaLabel,
          url,
        }
      : null,
  };
};
